import { UIIcon } from '../../../config/icons';
import { $on, $trigger, YEventHandlers, YInputEvent, YSelectedEvent, YValueEvent } from '../../y/events';
import { $format } from '../../y/format';
import { $i18n } from '../../y/i18n';
import { $date, $time, YDate, YTime } from '../../y/time';
import { $el } from '../elements';
import { onKey, UIKey } from '../key';
import { uiShowPopup } from '../popup';
import { UIRelPos } from '../types';
import { UIView } from '../view';
import { UIButton } from './button';
import { UIBaseInput, UIBaseInputOptions } from './input-base';
import { UIMultiSelect, UISelectOptions } from './select';

declare global {
    interface Element {
        ui_date_n: number;
    }
}

export interface UIDateInputOptions<T> extends UIBaseInputOptions {
    min?: T;
    max?: T;
}

function fillCalendar(calendar: HTMLElement, month: YDate, from_n?: number, to_n?: number) {
    calendar.ui_empty([1, 2, 3, 4, 5, 6, 0].map(d => $el.SPAN('r-weekday', $i18n.y.short_days[d])));

    const today_n = $date.today().n;
    const month_number = month.month;
    const end = month.add(1, $time.month);

    for(let date = month.floor($time.week); date.before(end);) {
        for(let i = 0; i < 7; i++) {
            const n = date.n;
            const d = date.day_of_month;
            const m = date.month;
            const span = $el.SPAN('r-day', d + '')
            if(m != month_number)
                span.classList.add('f-other');
            if(n == today_n)
                span.classList.add('f-today');
            if(from_n && to_n) {
                if(n >= from_n && n <= to_n) {
                    span.classList.add('f-selected');
                    if(n > from_n)
                        span.classList.add('f-extend-left');
                    if(n < to_n)
                        span.classList.add('f-extend-right');
                }
            }

            span.ui_date_n = n;
            date = date.add(1, $time.day);
            calendar.ui_add(span);
        }
    }
}

export class UIDatePicker extends UIView {
    $handlers: YEventHandlers<YInputEvent & YValueEvent<YDate>>;

    private _month!: YDate;
    private _title: HTMLElement;
    private _value: YDate;
    private _calendar: HTMLElement;

    constructor(value: YDate = $date.today()) {
        super('div', 'ui-date-picker');

        this.ui_add([
            $el.DIV('r-bar', [
                new UIButton({
                    kind: 'k-square', classes: 'm-round m-transparent', icon: UIIcon.ANGLE_LEFT,
                    on_click: () => { this.month = this._month.add(-1, $time.month) }
                }),
                this._title = $el.SPAN(),
                new UIButton({
                    kind: 'k-square', classes: 'm-round m-transparent', icon: UIIcon.ANGLE_RIGHT,
                    on_click: () => { this.month = this._month.add(1, $time.month) }
                })
            ]),
            this._calendar = $el.create('div', 'ui-calendar')
        ]);

        this._calendar.addEventListener('click', ev => {
            const date = (ev.target as HTMLElement).ui_date_n;
            if(date) {
                this.value = $date(date);
                $trigger(this, 'value', this._value);
                $trigger(this, 'input');
            }
        });

        this._value = value;
        this.month = value;
    }

    set month(month: YDate) {
        this._title.textContent = month.format('MMMM yyyy');
        this._month = month.floor($time.month);
        this._fillCalendar();
    }

    set value(value: YDate | undefined) {
        this._value = value || $date.today();
        this._fillCalendar();
    }

    get value() {
        return this._value;
    }

    private _fillCalendar() {
        fillCalendar(this._calendar, this._month, this._value?.n, this._value?.n);
    }
}

export class UIDateRangePicker extends UIView {
    $handlers: YEventHandlers<YInputEvent & YValueEvent<[YDate, YDate]> & YSelectedEvent<[YDate, YDate]>>;

    private _month!: YDate;
    private _title: HTMLElement;
    private _value?: [YDate, YDate];
    private _last_click?: YDate;
    private _calendar: HTMLElement;
    private _button?: UIButton;

    constructor(value?: [YDate, YDate], no_button?: boolean) {
        super('div', 'ui-date-picker');

        this.ui_add([
            $el.DIV('r-bar', [
                new UIButton({
                    kind: 'k-square', classes: 'm-round m-transparent', icon: UIIcon.ANGLE_LEFT,
                    on_click: () => { this.month = this._month.add(-1, $time.month) }
                }),
                this._title = $el.SPAN(),
                new UIButton({
                    kind: 'k-square', classes: 'm-round m-transparent', icon: UIIcon.ANGLE_RIGHT,
                    on_click: () => { this.month = this._month.add(1, $time.month) }
                })
            ]),
            this._calendar = $el.create('div', 'ui-calendar'),
            !no_button && (this._button = new UIButton({
                title: $i18n.ui.select_dates, classes: 'r-confirm ui-tint-reverse', on_click: () => {
                    if(this._value)
                        $trigger(this, 'selected', this._value);
                }
            }))
        ]);

        this._calendar.addEventListener('click', ev => {
            const date = (ev.target as HTMLElement).ui_date_n;
            if(date) {
                const d = $date(date);
                if(this._value) {
                    const [from, to] = this._value;
                    if(d.after(to))
                        this._setValue([from, d]);
                    else if(d.before(from))
                        this._setValue([d, to]);
                    else
                        this._setValue([this._last_click || from, d]);
                } else {
                    this._setValue([d, d])
                }
                this._last_click = d;
            }
        });

        this._value = value;
        this.month = value?.[0] || $date.today();
    }

    private _setValue([from, to]: [YDate, YDate]) {
        if(from.after(to))
            [from, to] = [to, from];
        this.value = [from, to];
        $trigger(this, 'value', this._value!);
        $trigger(this, 'input');
    }

    set month(month: YDate) {
        this._title.textContent = month.format('MMMM yyyy');
        this._month = month.floor($time.month);
        this._fillCalendar();
    }

    set value(value: [YDate, YDate] | undefined) {
        this._value = value;
        this._fillCalendar();
        if(value) {
            const [from, to] = value;
            const from_str = from.format('dd MMM').toLocaleUpperCase();
            if(from.equal(to)) {
                if(this._button)
                    this._button.title = from_str;
            } else {
                const to_str = to.format('dd MMM').toLocaleUpperCase();
                if(this._button)
                    this._button.title = [from_str + '\u00a0\u00a0\u00a0\u00a0', $el.ICON(UIIcon.ARROW_RIGHT), '\u00a0\u00a0\u00a0\u00a0' + to_str];
            }
        }
    }

    get value() {
        return this._value;
    }

    private _fillCalendar() {
        fillCalendar(this._calendar, this._month, this._value?.[0]?.n, this._value?.[1]?.n);
    }
}

type _date_input_type = 'year' | 'month' | 'day' | 'hour' | 'minute';

abstract class UIDateInputBase<T> extends UIBaseInput<T> {
    protected base_value!: YTime;
    initial_value?: YTime;
    private _date: boolean;
    private _time: boolean;

    private _year!: HTMLInputElement;
    private _month!: HTMLInputElement;
    private _day!: HTMLInputElement;
    private _hour!: HTMLInputElement;
    private _minute!: HTMLInputElement;

    constructor(options: UIDateInputOptions<any>, date: boolean, time: boolean) {
        if(date) {
            const button = $el.ICON(UIIcon.CALENDAR, 'u-clickable');
            options.r_deco = button;
            button.addEventListener('click', () => {
                const picker = new UIDatePicker(this.base_value.n ? this.base_value.date : undefined);
                $on(picker, 'value', v => {
                    let t = v.start;
                    if(t && time && this.base_value.n)
                        t = $time.make(v.year, v.month, v.day_of_month, this.base_value.hours, this.base_value.minutes);
                    this.setBaseValue(t);
                    this.onBaseValue();
                });
                uiShowPopup(picker, this, { position: UIRelPos.Below | UIRelPos.End, capture_mouse: true, can_flip: true })
            });
        }

        const div = $el.DIV();

        super(options, div);
        this.classList.add('ui-date-input');

        this._date = date;
        this._time = time;

        div.ui_add([
            options.placeholder && $el.DIV('r-placeholder', options.placeholder),
            date && [
                this._year = this._makeInput('year'),
                $el.SPAN('r-separator', '-'),
                this._month = this._makeInput('month'),
                $el.SPAN('r-separator', '-'),
                this._day = this._makeInput('day')
            ],
            time && [
                $el.SPAN('r-separator', '\u00a0\u00a0\u00a0'),
                this._hour = this._makeInput('hour'),
                $el.SPAN('r-separator', ':'),
                this._minute = this._makeInput('minute')
            ]
        ])

        div.addEventListener('click', () => {
            if(!this.base_value.n) {
                this.setBaseValue(this.initial_value || $time.now());
                this.onBaseValue();
                this._year.focus();
            }
        })

        this.setBaseValue($time(0));
    }

    private _makeInput(which: _date_input_type) {
        const input = $el.INPUT('text', 'r-input r-' + which);
        const digits = which === 'year' ? 4 : 2;
        input.placeholder = digits == 4 ? '\u2013\u2013\u2013\u2013' : '\u2013\u2013';
        input.addEventListener('input', () => {
            this.setDirty(true);

            const original = input.value || '';
            let text = original.replace(/\D/g, '');

            if(text.length > digits)
                text = text.substr(text.length - digits);

            if(text != original)
                input.value = text;

            if(text.length == digits)
                this._focusNext(which);
        });

        onKey(input, e => {
            if(e.key === UIKey.Down || e.key === UIKey.Up) {
                this.setDirty(true);
                const range = this._range(which);
                let value = parseInt(input.value);
                if(e.key === UIKey.Down) {
                    value--;
                    if(value < range[0]) value = range[1];
                } else {
                    value++;
                    if(value > range[1]) value = range[0];
                }
                input.value = $format.padNumber(value, digits);
                this._calcValue();

                return true;
            }

            return;
        })

        input.addEventListener('blur', (ev) => {
            const range = this._range(which);
            let value = parseInt(input.value) || 0;
            if(value < range[0])
                value = range[0];
            else if(value > range[1])
                value = range[1];
            input.value = $format.padNumber(value, digits);
            if(!ev.relatedTarget || !this.root.contains(ev.relatedTarget as HTMLElement)) {
                this.classList.remove('f-focus');
                $trigger(this, 'blur');
            }
            this._calcValue();
        });

        input.addEventListener('focus', () => {
            this.classList.add('f-focus');
            input.select();
        });

        return input;
    }

    private _calcValue() {
        const year = (this._year && parseInt(this._year.value)) || new Date().getFullYear();
        const month = (this._month && parseInt(this._month.value)) || 1;
        const day = (this._day && parseInt(this._day.value)) || 1;
        const hour = (this._hour && parseInt(this._hour.value)) || 0;
        const minute = (this._minute && parseInt(this._minute.value)) || 0;

        const before = this.base_value;
        this.base_value = $time.make(year, month - 1, day, hour, minute);
        if(before.n != this.base_value.n)
            this.onBaseValue();
    }

    protected setBaseValue(value: YTime) {
        this.base_value = value;

        if(value.n) {
            if(this._date) {
                this._year.value = $format.padNumber(value.year, 4);
                this._month.value = $format.padNumber(value.month + 1, 2);
                this._day.value = $format.padNumber(value.day_of_month, 2);
            }
            if(this._time) {
                this._hour.value = $format.padNumber(value.hours, 2);
                this._minute.value = $format.padNumber(value.minutes, 2);
            }
        }

        this.fixDesign();
        this.doCheck();
    }

    private _range(which: 'day' | 'month' | 'year' | 'hour' | 'minute'): [number, number] {
        switch(which) {
            case 'day':
                return this.base_value.n ? [1, new Date(this.base_value.year, this.base_value.month + 1, 0).getDate()] : [1, 31];
            case 'month':
                return [1, 12];
            case 'year':
                return [0, 9999];
            case 'hour':
                return [0, 23];
            default:
                return [0, 59];
        }
    }

    private _focusNext(which: _date_input_type) {
        switch(which) {
            case 'year':
                this._month.focus();
                break;
            case 'month':
                this._day.focus();
                break;
            case 'day':
                if(this._time) {
                    this._hour.focus();
                } else {
                    this._day.blur();
                }
                break;
            case 'hour':
                this._minute.focus();
                break;
            default:
                this._minute.blur();
        }
    }

    get is_empty() {
        return !this.base_value.n;
    }

    protected doCheck() {
        this.setOkay(!(this.required && !this.base_value.n));
    }

    abstract onBaseValue(): void;
}

export class UIDateTimeInput extends UIDateInputBase<YTime> {
    // private _min?: YTime;
    // private _max?: YTime;

    constructor(options: UIDateInputOptions<YTime> = {}) {
        super(options, true, true);
    }

    get value() {
        return this.base_value;
    }

    set value(v: YTime) {
        this.setBaseValue($time(v));
    }

    onBaseValue() {
        $trigger(this, 'value', this.value);
    }
}

export class UIDateInput extends UIDateInputBase<YDate> {
    // private _min?: YTime;
    // private _max?: YTime;

    constructor(options: UIDateInputOptions<YTime> = {}) {
        super(options, true, false);
    }

    get value() {
        return this.base_value.date;
    }

    set value(v: YDate) {
        this.setBaseValue($date(v).start);
    }

    onBaseValue() {
        $trigger(this, 'value', this.value);
    }
}

export class UIDateRangeInput extends UIBaseInput<[YDate, YDate] | undefined> {
    private _placeholder: string;
    private _value: [YDate, YDate] | undefined;

    constructor(options: UIBaseInputOptions = {}) {
        options.r_deco = $el.ICON(UIIcon.ANGLE_DOWN, 'r-arrow');
        const input = $el.DIV();
        super(options, input);
        this.classList.add('ui-select');
        input.tabIndex = 0;

        input.addEventListener('blur', () => {
            this.classList.remove('f-focus');
            $trigger(this, 'blur');
        });

        input.addEventListener('focus', () => {
            this.classList.add('f-focus');
            $trigger(this, 'focus');
        });

        this._placeholder = options.placeholder || '\u00a0';

        this.root.addEventListener('click', () => { this.showPicker(); });

        this.value = undefined;
    }

    get value() {
        return this._value;
    }

    set value(v: [YDate, YDate] | undefined) {
        this._value = v;

        this.fixText();
        this.doCheck();
        this.fixDesign();

        $trigger(this, 'value', this._value);
    }

    showPicker() {
        const picker = new UIDateRangePicker(this._value, true);
        $on(picker, 'value', v => {
            this.value = v;
        });
        uiShowPopup(picker, this, { position: UIRelPos.Below | UIRelPos.End, capture_mouse: true, can_flip: true })
    }

    get is_empty() {
        return !this._value;
    }

    protected fixText() {
        this.input_elem.ui_empty(this._value ? this._value[0].format("dd MMM") + " - " + this._value[1].format("dd MMM") : this._placeholder);
    }

    protected doCheck() {
        this.setOkay(!(this.required && !this._value));
    }
}

export class UIWeekdaysSelect extends UIMultiSelect<number> {
    constructor(options: UISelectOptions<number, number[]> = {}) {
        const values = [1, 2, 3, 4, 5, 6, 0].map(n => ({ key: n, name: $i18n.y.days[n] }));
        options.keep_open = true;
        options.display = value => {
            if(!value.length)
                return 'None';
            else if(value.length == 7)
                return 'All days';
            else
                return this._values.filter(v => value.includes(v.key)).map(v => v.name).join(', ');
        }
        super(values, options);
        this.value = [1, 2, 3, 4, 5, 6, 0];
    }
}
